import React from 'react';

import { Area, Column } from '@ant-design/charts';

import type { ChartView } from '../types';
import styles from '../charts.module.less';

export interface SeriesTrendDatum {
  period: string;
  /** Series name, as displayed in the tooltip. */
  category: string;
  value: number;
}

export interface SeriesTrendChartProps {
  data: SeriesTrendDatum[];
  view: ChartView;
  /** Colors in the same order as the series appear in `data`. */
  colors: string[];
  containerRef?: React.Ref<HTMLDivElement>;
  height?: number;
}

/** Several series per period, as grouped columns or overlapping areas. */
const SeriesTrendChart: React.FC<SeriesTrendChartProps> = ({
  data,
  view,
  colors,
  containerRef,
  height = 240,
}) => {
  const categories = Array.from(new Set(data.map(item => item.category)));
  const colorByCategory = new Map(
    categories.map((category, index) => [category, colors[index % colors.length]]),
  );
  const getColor = (datum: { category?: string }) =>
    colorByCategory.get(datum.category ?? '') ?? colors[0];
  const commonConfig = {
    data,
    xField: 'period',
    yField: 'value',
    seriesField: 'category',
    height,
    color: colors,
    animation: false,
    legend: false as const,
    tooltip: { shared: true },
  };

  return (
    <div ref={containerRef} className={styles.chart}>
      {view === 'bar' ? (
        <Column {...commonConfig} isGroup columnWidthRatio={0.72} maxColumnWidth={28} />
      ) : (
        <Area
          {...commonConfig}
          areaStyle={(datum: { category?: string }) => ({
            fill: `l(270) 0:${getColor(datum)} 1:#ffffff`,
            fillOpacity: 0.2,
          })}
          line={{
            color: getColor,
            style: { lineWidth: 2, lineCap: 'round', lineJoin: 'round' },
          }}
          point={{ size: 3, shape: 'circle' }}
          xAxis={{ label: { autoHide: true, autoRotate: false } }}
        />
      )}
    </div>
  );
};

export default SeriesTrendChart;
